const axios = require("axios");

/**
 * Public knowledgebase API used by the marketing site support docs.
 * Matches VUE_APP_API_ENDPOINT shape: .../api/knowledgebase/sites/<id>
 */
const KB_SITE_BASE =
  process.env.SUPPORT_SITEMAP_API_BASE ||
  process.env.VUE_APP_API_ENDPOINT ||
  "https://api.transpond.io/api/knowledgebase/sites/2";

function collectionPathId(collection) {
  return collection.slug || String(collection.id);
}

function categoryPathId(category) {
  return category.slug || String(category.id);
}

function articlePathId(article) {
  return article.slug || String(article.id);
}

function isPublishedArticle(article) {
  return !article.status || article.status === "published";
}

function isIndexableCategory(category) {
  return (category.name || "").trim().toLowerCase() !== "uncategorized";
}

async function fetchJson(path) {
  const response = await axios.get(`${KB_SITE_BASE}${path}`);
  return response.data;
}

async function getCollections() {
  const collections = await fetchJson("/collections");
  return Array.isArray(collections) ? collections : [];
}

/**
 * Sitemap/prerender slugs for `/support/:collectionId`.
 * @returns {Promise<Array<{ collectionId: string }>>}
 */
async function getCollectionSlugs() {
  const collections = await getCollections();
  return collections.map((collection) => ({
    collectionId: collectionPathId(collection),
    ...(collection.updatedAt ? { lastmod: collection.updatedAt } : {}),
  }));
}

/**
 * Sitemap/prerender slugs for `/support/:collectionId/category/:categoryId`.
 * @returns {Promise<Array<{ collectionId: string, categoryId: string }>>}
 */
async function getCategorySlugs() {
  const collections = await getCollections();
  const slugs = [];

  for (const collection of collections) {
    const categories = await fetchJson(
      `/collections/${collection.id}/categories`
    );
    const collectionId = collectionPathId(collection);

    for (const category of Array.isArray(categories) ? categories : []) {
      if (!isIndexableCategory(category)) {
        continue;
      }

      slugs.push({
        collectionId,
        categoryId: categoryPathId(category),
        ...(category.updatedAt ? { lastmod: category.updatedAt } : {}),
      });
    }
  }

  return slugs;
}

/**
 * Sitemap/prerender slugs for `/support/:collectionId/:articleId`.
 * @returns {Promise<Array<{ collectionId: string, articleId: string }>>}
 */
async function getArticleSlugs() {
  const collections = await getCollections();
  const slugs = [];

  for (const collection of collections) {
    const articles = await fetchJson(`/collections/${collection.id}/articles`);
    const collectionId = collectionPathId(collection);

    for (const article of Array.isArray(articles) ? articles : []) {
      if (!isPublishedArticle(article)) {
        continue;
      }

      slugs.push({
        collectionId,
        articleId: articlePathId(article),
        ...(article.updatedAt ? { lastmod: article.updatedAt } : {}),
      });
    }
  }

  return slugs;
}

module.exports = {
  KB_SITE_BASE,
  getArticleSlugs,
  getCategorySlugs,
  getCollectionSlugs,
};
